import { config } from '../../config/env';

/**
 * OpenAI API Configuration for Configuration Generation
 * Specific configuration for generating VR session configurations
 * Uses the same config source as translations client
 */
export const OPENAI_CONFIG = {
  // Trim the API key to remove any whitespace or hidden characters
  API_KEY: (config.OPENAI_API_KEY || '').trim(),
  API_URL: 'https://api.openai.com/v1/chat/completions',
  MODEL: 'gpt-5-mini', // Most economical model for configuration generation
  TEMPERATURE: 0.7, // Higher temperature for more creative configurations
  MAX_TOKENS: 2000, // Sufficient for generating complete configurations
} as const;

/**
 * Call OpenAI Chat Completions API for configuration generation
 * This is a feature-specific wrapper around OpenAI API
 */
export const callOpenAI = async (
  messages: Array<{ role: string; content: string }>,
  options?: {
    model?: string;
    temperature?: number;
    maxTokens?: number;
  }
): Promise<{
  content: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}> => {
  // Debug: verifica che la chiave API sia presente (senza loggarla per sicurezza)
  const apiKey = OPENAI_CONFIG.API_KEY.trim();
  if (!apiKey || apiKey === '') {
    console.error('❌ OPENAI_CONFIG.API_KEY is empty or undefined');
    throw new Error(
      'OpenAI API key is not configured. Please set VITE_OPENAI_API_KEY in your .env.local file.'
    );
  }

  const model = options?.model || OPENAI_CONFIG.MODEL;
  const maxTokens = options?.maxTokens || OPENAI_CONFIG.MAX_TOKENS;
  const body: Record<string, unknown> = { model, messages };

  if (model.startsWith('gpt-5')) {
    body.max_completion_tokens = maxTokens;
    body.reasoning_effort = 'minimal';
  } else {
    body.temperature = options?.temperature ?? OPENAI_CONFIG.TEMPERATURE;
    body.max_tokens = maxTokens;
  }

  const response = await fetch(OPENAI_CONFIG.API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `OpenAI API error: ${response.status}`;

    try {
      const errorJson = JSON.parse(errorText);
      if (errorJson.error?.message) {
        errorMessage = `OpenAI API error: ${response.status} - ${errorJson.error.message}`;
      } else {
        errorMessage = `OpenAI API error: ${response.status} - ${errorText}`;
      }
    } catch {
      errorMessage = `OpenAI API error: ${response.status} - ${errorText}`;
    }

    throw new Error(errorMessage);
  }

  const data = await response.json();

  return {
    content: data.choices[0]?.message?.content || '',
    usage: {
      promptTokens: data.usage?.prompt_tokens || 0,
      completionTokens: data.usage?.completion_tokens || 0,
      totalTokens: data.usage?.total_tokens || 0,
    },
  };
};
